import { useEffect, useState } from "react";
import { Plus, X } from "lucide-react";
import { Field, TextInput, TextArea, SelectInput } from "@/components/brand/Field";
import { api, formatApiError } from "@/lib/api";

const STATUSES = ["Requested", "Confirmed", "Completed", "Cancelled"];

const AdminBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [services, setServices] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [f, setF] = useState({ name: "", email: "", phone: "", service_slug: "", preferred_at: "", timezone: "", notes: "" });
  const [error, setError] = useState("");

  const load = () => api.get("/admin/bookings").then((r) => setBookings(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/services?limit=200").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  const add = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/admin/bookings", { ...f, service_slug: f.service_slug || null, preferred_at: f.preferred_at || null, phone: f.phone || null, timezone: f.timezone || null, notes: f.notes || null });
      setShowForm(false);
      setF({ name: "", email: "", phone: "", service_slug: "", preferred_at: "", timezone: "", notes: "" });
      load();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };

  const setStatus = async (b, status) => {
    await api.patch(`/admin/bookings/${b.id}/status`, { status });
    load();
  };

  return (
    <div className="space-y-6" data-testid="admin-bookings">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="space-y-1">
          <h1 className="font-display text-2xl font-bold text-brand-snow sm:text-3xl">Discovery-call bookings</h1>
          <p className="text-sm text-brand-mist">Calls confirmed from enquiries are recorded and tracked here.</p>
        </div>
        <button onClick={() => setShowForm(true)} data-testid="booking-new" className="inline-flex items-center gap-2 rounded-full bg-brand-emerald px-5 py-2.5 font-display text-sm font-semibold text-brand-black hover:bg-brand-snow">
          <Plus size={15} /> Add booking
        </button>
      </div>

      <div className="surface-card overflow-x-auto" data-testid="bookings-table">
        <table className="w-full min-w-[680px] text-left text-sm">
          <thead>
            <tr className="border-b border-brand-mist/10 text-xs uppercase tracking-wider text-brand-mist">
              <th className="px-5 py-3.5 font-medium">Contact</th>
              <th className="px-5 py-3.5 font-medium">Service</th>
              <th className="px-5 py-3.5 font-medium">Preferred time</th>
              <th className="px-5 py-3.5 font-medium">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-brand-mist/8">
            {bookings.length === 0 && <tr><td colSpan={4} className="px-5 py-8 text-center text-brand-mist">No bookings yet. Add one when a call is confirmed with an enquirer.</td></tr>}
            {bookings.map((b) => (
              <tr key={b.id} data-testid={`booking-row-${b.id}`} className="transition-colors hover:bg-white/5">
                <td className="px-5 py-3.5">
                  <p className="font-medium text-brand-snow">{b.name}</p>
                  <p className="text-xs text-brand-mist">{b.email}{b.phone ? ` · ${b.phone}` : ""}</p>
                </td>
                <td className="px-5 py-3.5 text-brand-mist">{b.service_slug || "—"}</td>
                <td className="px-5 py-3.5 text-brand-mist">{b.preferred_at || "—"}{b.timezone ? ` (${b.timezone})` : ""}</td>
                <td className="px-5 py-3.5">
                  <select
                    value={b.status}
                    onChange={(e) => setStatus(b, e.target.value)}
                    data-testid={`booking-status-${b.id}`}
                    className="rounded-md border border-brand-mist/20 bg-brand-charcoal px-2 py-1.5 text-xs text-brand-snow focus:border-brand-emerald focus:outline-none"
                  >
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex justify-center bg-brand-black/70 backdrop-blur-sm sm:p-6" onClick={() => setShowForm(false)} data-testid="booking-form-modal">
          <form onSubmit={add} className="h-full w-full max-w-xl space-y-5 overflow-y-auto border border-brand-mist/15 bg-brand-charcoal p-6 sm:h-auto sm:rounded-2xl sm:p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-bold text-brand-snow">Add booking</h2>
              <button type="button" onClick={() => setShowForm(false)} aria-label="Close" className="rounded-md p-2 text-brand-mist hover:text-brand-snow"><X size={18} /></button>
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Name" htmlFor="bk-name" required><TextInput id="bk-name" testid="bk-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required /></Field>
              <Field label="Email" htmlFor="bk-email" required><TextInput id="bk-email" type="email" testid="bk-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required /></Field>
              <Field label="Phone" htmlFor="bk-phone"><TextInput id="bk-phone" testid="bk-phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></Field>
              <Field label="Service" htmlFor="bk-service">
                <SelectInput id="bk-service" testid="bk-service" value={f.service_slug} onChange={(e) => setF({ ...f, service_slug: e.target.value })}>
                  <option value="">General / not sure</option>
                  {services.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
                </SelectInput>
              </Field>
              <Field label="Preferred time" htmlFor="bk-time">
                <input id="bk-time" type="datetime-local" data-testid="bk-time" value={f.preferred_at} onChange={(e) => setF({ ...f, preferred_at: e.target.value })} className="w-full rounded-md border border-brand-mist/20 bg-brand-charcoal px-4 py-3 text-sm text-brand-snow [color-scheme:dark] focus:border-brand-emerald focus:outline-none" />
              </Field>
              <Field label="Timezone" htmlFor="bk-tz"><TextInput id="bk-tz" testid="bk-tz" value={f.timezone} onChange={(e) => setF({ ...f, timezone: e.target.value })} placeholder="Australia/Sydney" /></Field>
            </div>
            <Field label="Notes" htmlFor="bk-notes"><TextArea id="bk-notes" rows={3} testid="bk-notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field>
            {error && <p role="alert" className="text-sm text-red-400">{error}</p>}
            <button type="submit" data-testid="bk-save" className="rounded-full bg-brand-emerald px-6 py-3 font-display text-sm font-semibold text-brand-black hover:bg-brand-snow">Add booking</button>
          </form>
        </div>
      )}
    </div>
  );
};

export default AdminBookings;
